import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { matchesAll } from "@/lib/trFold";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Truck, UserCheck, MapPin, ExternalLink, Loader2, Signature as SignatureIcon,
  Plus, Copy, Package, Clock, Pencil, Printer, FileText, Receipt, Search,
} from "lucide-react";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import ShipmentPhotoUploader from "@/components/ShipmentPhotoUploader";
import ShipmentLineChecklist from "@/components/ShipmentLineChecklist";
import BufferedPhotoPicker from "@/components/BufferedPhotoPicker";

const COURIER_TEMPLATES = [
  { name: "Aras Kargo", url: (no) => `https://kargotakip.araskargo.com.tr/mainpage.aspx?code=${no}` },
  { name: "Yurtiçi Kargo", url: (no) => `https://www.yurticikargo.com/tr/online-servisler/gonderi-sorgula?code=${no}` },
  { name: "MNG Kargo", url: (no) => `https://kargotakip.mngkargo.com.tr/?takipno=${no}` },
  { name: "PTT Kargo", url: (no) => `https://gonderitakip.ptt.gov.tr/Track/Verify?q=${no}` },
  { name: "Sürat Kargo", url: (no) => `https://www.suratkargo.com.tr/KargoTakip/?kargotakipno=${no}` },
  { name: "UPS", url: (no) => `https://www.ups.com/track?tracknum=${no}` },
  { name: "DHL", url: (no) => `https://www.dhl.com/tr-tr/home/tracking/tracking-express.html?tracking-id=${no}` },
];

const trackingUrlFor = (courier, no) => {
  const tpl = COURIER_TEMPLATES.find((t) => t.name === courier);
  return tpl && no ? tpl.url(no) : "";
};

const summarizeSizes = (sb) => {
  if (!sb) return "";
  return Object.entries(sb).map(([k, v]) => `${k}:${v}`).join(", ");
};

export default function Shipments() {
  const [tab, setTab] = useState("bekliyor");
  const [pending, setPending] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loadingP, setLoadingP] = useState(true);
  const [loadingS, setLoadingS] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [newPrefill, setNewPrefill] = useState(null);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const loadPending = async () => {
    setLoadingP(true);
    try {
      const { data } = await api.get(`/shipments/pending`);
      setPending(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingP(false); }
  };

  const loadShipments = async () => {
    setLoadingS(true);
    try {
      const { data } = await api.get(`/shipments/`);
      setShipments(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingS(false); }
  };

  useEffect(() => { loadPending(); loadShipments(); }, []);

  const startShipFor = (o) => {
    setNewPrefill({
      order_id: o.id,
      order_no: o.order_no,
      customer_name: o.customer_name,
      product_summary: o.product_name,
      size_summary: summarizeSizes(o.size_breakdown),
    });
    setOpenNew(true);
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return shipments;
    return shipments.filter((s) =>
      matchesAll(q, [s.order_no, s.tracking_no, s.courier_name, s.signer_name, s.product_summary, s.prepared_by, s.invoice_no, s.waybill_no])
    );
  }, [shipments, q]);

  return (
    <div>
      <PageHeader
        title="Sevkiyat & Teslimat"
        subtitle="Sevkiyat bekleyen siparişler, kargo & elden teslimat kayıtları"
        breadcrumbs={["Ana Sayfa", "Sevkiyat"]}
        actions={
          <Button data-testid="new-shipment-btn" onClick={() => { setNewPrefill(null); setOpenNew(true); }}
            className="bg-blue-600 hover:bg-blue-500 rounded-sm" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Yeni Sevkiyat
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-900 border border-slate-800 rounded-sm" data-testid="shipment-tabs">
            <TabsTrigger data-testid="tab-bekliyor" value="bekliyor" className="rounded-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Clock className="w-4 h-4 mr-1.5" /> Sevkiyat Bekliyor
              <Badge className="ml-2 bg-slate-800 text-slate-200 border-slate-700 rounded-sm text-[10px]">{pending.length}</Badge>
            </TabsTrigger>
            <TabsTrigger data-testid="tab-kargo" value="kargo" className="rounded-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Truck className="w-4 h-4 mr-1.5" /> Kargo Teslimatları
            </TabsTrigger>
            <TabsTrigger data-testid="tab-elden" value="elden" className="rounded-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <UserCheck className="w-4 h-4 mr-1.5" /> Elden Teslimatlar
            </TabsTrigger>
          </TabsList>

          {/* -- Sevkiyat Bekliyor -- */}
          <TabsContent value="bekliyor" className="mt-5">
            <PendingTable list={pending} loading={loadingP} onShip={startShipFor} onReload={loadPending} />
          </TabsContent>

          {/* -- Kargo -- */}
          <TabsContent value="kargo" className="mt-5 space-y-3">
            <SearchBar value={q} onChange={setQ} />
            <ShipmentTable
              rows={filtered.filter((s) => s.delivery_mode === "kargo")}
              loading={loadingS}
              mode="kargo"
              onEdit={setEditing}
            />
          </TabsContent>

          {/* -- Elden -- */}
          <TabsContent value="elden" className="mt-5 space-y-3">
            <SearchBar value={q} onChange={setQ} />
            <ShipmentTable
              rows={filtered.filter((s) => s.delivery_mode === "elden")}
              loading={loadingS}
              mode="elden"
              onEdit={setEditing}
            />
          </TabsContent>
        </Tabs>
      </div>

      <ShipmentDialog
        open={openNew}
        onOpenChange={setOpenNew}
        prefill={newPrefill}
        onSaved={() => { setOpenNew(false); loadPending(); loadShipments(); }}
      />
      <EditShipmentDialog
        shipment={editing}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        onSaved={() => { setEditing(null); loadShipments(); }}
      />
    </div>
  );
}

function SearchBar({ value, onChange }) {
  return (
    <div className="relative max-w-md">
      <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-500" />
      <Input value={value} onChange={(e) => onChange(e.target.value)}
        data-testid="shipment-search"
        placeholder="Sipariş no, takip no, müşteri, personel…"
        className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm h-8 pl-8 text-xs" />
    </div>
  );
}

function PendingTable({ list, loading, onShip, onReload }) {
  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>;
  }
  if (list.length === 0) {
    return (
      <Card className="bg-slate-900 border-slate-800 rounded-sm p-10 text-center" data-testid="pending-empty">
        <Package className="w-10 h-10 text-slate-700 mx-auto mb-3" />
        <div className="text-sm text-slate-400">Sevkiyat bekleyen sipariş yok. Paketleme/Koli Hazırlık/İrsaliye aşamasındaki siparişler burada görünür.</div>
      </Card>
    );
  }
  return (
    <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-slate-800">
            {["Sipariş", "Müşteri", "Ürün", "Adet", "Bedenler", "Termin", "Aşama", ""].map((h) => (
              <TableHead key={h} className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.map((o) => (
            <TableRow key={o.id} className="border-slate-800" data-testid={`pending-row-${o.id}`}>
              <TableCell className="font-mono text-xs text-blue-400">{o.order_no}</TableCell>
              <TableCell className="text-sm text-slate-100">{o.customer_name}</TableCell>
              <TableCell className="text-sm text-slate-200">{o.product_name}</TableCell>
              <TableCell className="text-sm font-mono">{o.quantity}</TableCell>
              <TableCell className="text-xs text-slate-400 font-mono">{summarizeSizes(o.size_breakdown) || "—"}</TableCell>
              <TableCell className="text-xs text-slate-400 font-mono">{o.due_date ? new Date(o.due_date).toLocaleDateString("tr-TR") : "—"}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Badge className="bg-amber-950/40 border border-amber-900 text-amber-300 rounded-sm text-[10px] uppercase tracking-wider">{o.stage}</Badge>
                  {Number(o.shipped_total || 0) > 0 && Number(o.shipped_total || 0) < Number(o.quantity || 0) && (
                    <Badge data-testid={`partial-ship-badge-${o.id}`}
                      className="bg-cyan-950/50 border border-cyan-800 text-cyan-300 rounded-sm text-[10px] uppercase tracking-wider">
                      Kısmi Sevk {o.shipped_total}/{o.quantity}
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" onClick={() => onShip(o)}
                  data-testid={`ship-now-${o.id}`}
                  className="bg-emerald-600 hover:bg-emerald-500 rounded-sm h-7 text-xs uppercase tracking-wider font-semibold">
                  <Truck className="w-3.5 h-3.5 mr-1" /> Sevk Et
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function ShipmentTable({ rows, loading, mode, onEdit }) {
  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></div>;
  }
  if (rows.length === 0) {
    return (
      <Card className="bg-slate-900 border-slate-800 rounded-sm p-10 text-center">
        <Package className="w-10 h-10 text-slate-700 mx-auto mb-3" />
        <div className="text-sm text-slate-400">Henüz {mode === "kargo" ? "kargo" : "elden"} teslimat kaydı yok.</div>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent border-slate-800">
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Sipariş</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Müşteri</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ürün</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Renk</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Bedenler</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Paket</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Koli</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Hazırlayan</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Sevk Tarihi</TableHead>
            {mode === "kargo" ? (
              <>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kargo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Takip No</TableHead>
              </>
            ) : (
              <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Teslim Alan</TableHead>
            )}
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Durum</TableHead>
            <TableHead className="text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((s) => (
            <TableRow key={s.id} className="border-slate-800" data-testid={`shipment-row-${s.id}`}>
              <TableCell className="font-mono text-xs text-blue-400 whitespace-nowrap">{s.order_no}</TableCell>
              <TableCell className="text-sm text-slate-100 whitespace-nowrap">
                {s.customer_invoice_title || s.customer_name || "—"}
              </TableCell>
              <TableCell className="text-sm text-slate-200 max-w-[180px] truncate">{s.product_summary || "—"}</TableCell>
              <TableCell className="text-xs text-slate-400 whitespace-nowrap">{s.color || "—"}</TableCell>
              <TableCell className="text-xs font-mono text-slate-400 max-w-[120px] truncate">{s.size_summary || "—"}</TableCell>
              <TableCell className="text-xs text-slate-200 text-right font-mono">{s.package_count || 0}</TableCell>
              <TableCell className="text-xs text-slate-200 text-right font-mono">{s.box_count || 0}</TableCell>
              <TableCell className="text-xs text-slate-400 whitespace-nowrap">{s.prepared_by || "—"}</TableCell>
              <TableCell className="text-xs text-slate-400 font-mono whitespace-nowrap">
                {s.signed_at ? new Date(s.signed_at).toLocaleDateString("tr-TR") : "—"}
              </TableCell>
              {mode === "kargo" ? (
                <>
                  <TableCell className="text-xs text-slate-200 whitespace-nowrap">{s.courier_name || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-200 font-mono whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {s.tracking_no ? (
                        <>
                          <span className="truncate max-w-[100px]">{s.tracking_no}</span>
                          <button data-testid={`copy-tracking-${s.id}`}
                            onClick={() => { navigator.clipboard.writeText(s.tracking_no); toast.success("Kopyalandı"); }}
                            className="text-slate-500 hover:text-blue-400 shrink-0"><Copy className="w-3 h-3" /></button>
                        </>
                      ) : "—"}
                    </div>
                  </TableCell>
                </>
              ) : (
                <TableCell className="text-xs text-slate-200 whitespace-nowrap">
                  {s.signer_name || "—"} {s.signer_role ? <span className="text-slate-500 text-[10px]">({s.signer_role})</span> : null}
                </TableCell>
              )}
              <TableCell>
                <Badge className={mode === "kargo"
                  ? "bg-blue-950/40 border border-blue-900 text-blue-300 rounded-sm text-[10px] uppercase tracking-wider"
                  : "bg-emerald-950/40 border border-emerald-900 text-emerald-300 rounded-sm text-[10px] uppercase tracking-wider"}>
                  {mode === "kargo" ? "Kargoda" : "Teslim"}
                </Badge>
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <RowActions s={s} onEdit={onEdit} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

function RowActions({ s, onEdit }) {
  const trackingUrl = s.tracking_url || trackingUrlFor(s.courier_name, s.tracking_no);
  const btnCls = "h-7 w-7 p-0 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-sm inline-flex items-center justify-center";
  return (
    <div className="flex items-center gap-1 justify-end">
      <button data-testid={`edit-shipment-${s.id}`} onClick={() => onEdit(s)} className={btnCls} title="Düzenle">
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <button data-testid={`print-shipment-${s.id}`} onClick={() => window.print()} className={btnCls} title="Yazdır">
        <Printer className="w-3.5 h-3.5" />
      </button>
      <a href={`/api/exports/pdf/orders/${s.order_id}`} target="_blank" rel="noreferrer"
        data-testid={`waybill-${s.id}`} className={btnCls} title="İrsaliye PDF">
        <FileText className="w-3.5 h-3.5" />
      </a>
      <a href={`/api/exports/pdf/orders/${s.order_id}?variant=invoice`} target="_blank" rel="noreferrer"
        data-testid={`invoice-${s.id}`} className={btnCls} title="Fatura PDF">
        <Receipt className="w-3.5 h-3.5" />
      </a>
      {trackingUrl && (
        <a href={trackingUrl} target="_blank" rel="noreferrer"
          data-testid={`track-shipment-${s.id}`}
          className="h-7 w-7 p-0 text-blue-400 hover:text-blue-300 hover:bg-blue-950/30 rounded-sm inline-flex items-center justify-center"
          title="Kargo Takip">
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
}

function EditShipmentDialog({ shipment, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!shipment) return;
    setForm({
      status: shipment.status || "hazirlaniyor",
      courier_name: shipment.courier_name || "",
      tracking_no: shipment.tracking_no || "",
      tracking_url: shipment.tracking_url || "",
      package_count: shipment.package_count || 0,
      box_count: shipment.box_count || 0,
      prepared_by: shipment.prepared_by || "",
      color: shipment.color || "",
      size_summary: shipment.size_summary || "",
      product_summary: shipment.product_summary || "",
      invoice_no: shipment.invoice_no || "",
      waybill_no: shipment.waybill_no || "",
      signer_name: shipment.signer_name || "",
      signer_role: shipment.signer_role || "",
      notes: shipment.notes || "",
      delivered_to_person: shipment.delivered_to_person || "",
      delivered_at: (shipment.delivered_at || "").slice(0, 16) || "",
      delivered_by_employee_name: shipment.delivered_by_employee_name || "",
    });
  }, [shipment]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        package_count: Number(form.package_count) || 0,
        box_count: Number(form.box_count) || 0,
      };
      // datetime-local → ISO
      if (payload.delivered_at) {
        try { payload.delivered_at = new Date(payload.delivered_at).toISOString(); } catch {}
      }
      await api.patch(`/shipments/${shipment.id}`, payload);
      toast.success("Sevkiyat güncellendi");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!shipment || !form) return null;

  return (
    <Dialog open={!!shipment} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="edit-shipment-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-50 flex items-center gap-2">
            <Pencil className="w-5 h-5 text-blue-500" /> Sevkiyat Düzenle · {shipment.order_no}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 flex items-center gap-3 border border-slate-800 bg-slate-900/40 rounded-sm p-2">
            <Label className="text-xs uppercase text-slate-400 font-mono">Sevkiyat Durumu</Label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="bg-slate-950 border border-slate-700 rounded-sm px-2 py-1 text-sm" data-testid="edit-shipment-status">
              <option value="hazirlaniyor">Hazırlanıyor</option>
              <option value="kargoya_verildi">Kargoya Verildi (mail gönderilir)</option>
              <option value="teslim_edildi">Teslim Edildi (mail gönderilir)</option>
              <option value="iptal">İptal</option>
            </select>
            <span className="text-[10px] text-amber-300 ml-auto">Statü değişince müşteriye otomatik mail gönderilir.</span>
          </div>
          <FieldInput label="Renk" value={form.color} onChange={(v) => setForm({ ...form, color: v })} />
          <FieldInput label="Beden Özeti" value={form.size_summary} onChange={(v) => setForm({ ...form, size_summary: v })} placeholder="S:20, M:40" />
          <FieldInput label="Paket Sayısı" type="number" value={form.package_count} onChange={(v) => setForm({ ...form, package_count: v })} />
          <FieldInput label="Koli Sayısı" type="number" value={form.box_count} onChange={(v) => setForm({ ...form, box_count: v })} />
          <FieldInput label="Hazırlayan" value={form.prepared_by} onChange={(v) => setForm({ ...form, prepared_by: v })} />
          <FieldInput label="İrsaliye No" value={form.waybill_no} onChange={(v) => setForm({ ...form, waybill_no: v })} />
          <FieldInput label="Fatura No" value={form.invoice_no} onChange={(v) => setForm({ ...form, invoice_no: v })} />
          {shipment.delivery_mode === "kargo" ? (
            <>
              <FieldInput label="Kargo Firması" value={form.courier_name} onChange={(v) => setForm({ ...form, courier_name: v })} />
              <FieldInput label="Takip No" value={form.tracking_no} onChange={(v) => setForm({ ...form, tracking_no: v })} />
            </>
          ) : (
            <>
              <FieldInput label="Teslim Alan" value={form.signer_name} onChange={(v) => setForm({ ...form, signer_name: v })} />
              <FieldInput label="Rol" value={form.signer_role} onChange={(v) => setForm({ ...form, signer_role: v })} />
              <FieldInput label="Kime Teslim Edildi (Zorunlu)" value={form.delivered_to_person} onChange={(v) => setForm({ ...form, delivered_to_person: v })} />
              <FieldInput label="Teslim Tarihi/Saati (Zorunlu)" type="datetime-local" value={form.delivered_at} onChange={(v) => setForm({ ...form, delivered_at: v })} />
              <FieldInput label="Teslimi Yapan Personel (Zorunlu)" value={form.delivered_by_employee_name} onChange={(v) => setForm({ ...form, delivered_by_employee_name: v })} />
            </>
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase text-slate-500 font-mono">Notlar / Açıklama</Label>
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm resize-none" />
        </div>

        <div className="space-y-4 border-t border-slate-800 pt-4">
          <div className="text-xs uppercase tracking-widest font-mono text-blue-300 border-b border-blue-900/40 pb-1.5">
            📎 Sevk Evrağı & Kargo Fotoğrafları
          </div>
          <ShipmentPhotoUploader shipmentId={shipment.id} kind="document" />
          <ShipmentPhotoUploader shipmentId={shipment.id} kind="cargo" />
        </div>

        <ShipmentEmailPanel shipmentId={shipment.id} deliveryMode={shipment.delivery_mode} />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="border-slate-800 bg-slate-900 text-slate-300 hover:bg-slate-800 rounded-sm">İptal</Button>
          <Button onClick={save} disabled={saving} data-testid="save-shipment-edit-btn"
            className="bg-blue-600 hover:bg-blue-500 rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldInput({ label, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase text-slate-500 font-mono">{label}</Label>
      <Input type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm h-9" />
    </div>
  );
}

function ShipmentDialog({ open, onOpenChange, onSaved, prefill }) {
  const [mode, setMode] = useState("kargo");
  const [orders, setOrders] = useState([]);
  const [orderId, setOrderId] = useState("");
  const [savedShipmentId, setSavedShipmentId] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signerRole, setSignerRole] = useState("musteri");
  const [courierName, setCourierName] = useState("");
  const [trackingNo, setTrackingNo] = useState("");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [packageCount, setPackageCount] = useState(0);
  const [boxCount, setBoxCount] = useState(0);
  const [preparedBy, setPreparedBy] = useState("");
  const [color, setColor] = useState("");
  const [sizeSummary, setSizeSummary] = useState("");
  const [productSummary, setProductSummary] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [waybillNo, setWaybillNo] = useState("");
  const [gps, setGps] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sigRef, setSigRef] = useState(null);
  const [docFiles, setDocFiles] = useState([]);
  const [cargoFiles, setCargoFiles] = useState([]);
  const [shipItems, setShipItems] = useState(null); // kısmi sevk: [{line_id, qty}]

  useEffect(() => {
    if (!open) return;
    // Apply prefill from "Sevk Et" action
    if (prefill?.order_id) {
      setOrderId(prefill.order_id);
      setProductSummary(prefill.product_summary || "");
      setSizeSummary(prefill.size_summary || "");
    }
  }, [open, prefill]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/orders");
        setOrders((data || []).filter((o) => o.stage !== "tamamlandi"));
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [open]);

  // Kısmi sevk özeti — stabilize callback (yeni array referansı sonsuz
  // render döngüsüne yol açmasın diye eşitlik kontrollü)
  const handleSummaryChange = useCallback(({ productSummary: ps, sizeSummary: ss, items }) => {
    if (ps !== undefined) setProductSummary((prev) => (prev === ps ? prev : ps));
    if (ss !== undefined) setSizeSummary((prev) => (prev === ss ? prev : ss));
    if (items !== undefined) {
      setShipItems((prev) => (JSON.stringify(prev) === JSON.stringify(items) ? prev : items));
    }
  }, []);

  const captureGps = () => {
    if (!("geolocation" in navigator)) { toast.error("GPS desteklenmiyor"); return; }
    toast.loading("Konum alınıyor…", { id: "geo-ship" });    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy });
        toast.success(`Konum kaydedildi (±${Math.round(pos.coords.accuracy)}m)`, { id: "geo-ship" });
      },
      (err) => toast.error(`Konum alınamadı: ${err.message}`, { id: "geo-ship" }),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const applyCourierTemplate = (name) => {
    setCourierName(name);
    const tpl = COURIER_TEMPLATES.find((t) => t.name === name);
    if (tpl && trackingNo) setTrackingUrl(tpl.url(trackingNo));
  };
  useEffect(() => {
    const tpl = COURIER_TEMPLATES.find((t) => t.name === courierName);
    if (tpl && trackingNo) setTrackingUrl(tpl.url(trackingNo));
  }, [trackingNo, courierName]);

  const reset = () => {
    setOrderId(""); setSavedShipmentId("");
    setSignerName(""); setCourierName("");
    setTrackingNo(""); setTrackingUrl(""); setNotes(""); setGps(null);
    setPackageCount(0); setBoxCount(0); setPreparedBy(""); setColor("");
    setSizeSummary(""); setProductSummary(""); setInvoiceNo(""); setWaybillNo("");
    setDocFiles([]); setCargoFiles([]);
    setShipItems(null);
    if (sigRef) sigRef.clear();
  };

  const save = async () => {
    if (!orderId) return toast.error("Sipariş seçin");
    if (!signerName.trim()) return toast.error("Teslim alan / gönderen adı gerekli");
    if (shipItems !== null && shipItems.length === 0) return toast.error("Sevk edilecek ürün seçilmedi");
    let signature_data_url = "";
    if (mode === "elden") {
      if (!sigRef || sigRef.isEmpty()) return toast.error("Elden teslimat için imza gerekli");
      signature_data_url = sigRef.toDataURL("image/png");
    }
    if (mode === "kargo" && !courierName && !trackingNo) return toast.error("Kargo firması veya takip numarası gerekli");

    setSaving(true);
    try {
      const { data: created } = await api.post("/shipments/signatures", {
        order_id: orderId,
        signer_name: signerName,
        signer_role: signerRole,
        signature_data_url,
        notes,
        delivery_mode: mode,
        courier_name: courierName,
        tracking_no: trackingNo,
        tracking_url: trackingUrl,
        package_count: Number(packageCount) || 0,
        box_count: Number(boxCount) || 0,
        prepared_by: preparedBy,
        color, size_summary: sizeSummary, product_summary: productSummary,
        invoice_no: invoiceNo, waybill_no: waybillNo,
        latitude: gps?.lat ?? null, longitude: gps?.lng ?? null, accuracy_m: gps?.acc ?? null,
        ...(shipItems ? { items: shipItems } : {}),
      });
      const newId = created?.id || "";
      setSavedShipmentId(newId);

      // Upload buffered photos in the same flow (client-side compression was
      // already applied by BufferedPhotoPicker). Failures don't block the
      // shipment save.
      const uploads = [
        ...docFiles.map((f) => ({ kind: "document", file: f })),
        ...cargoFiles.map((f) => ({ kind: "cargo", file: f })),
      ];
      let ok = 0, fail = 0;
      if (newId && uploads.length) {
        toast.loading(`Fotoğraflar yükleniyor (${uploads.length})…`, { id: "ship-photos" });
        for (const u of uploads) {
          try {
            const fd = new FormData();
            fd.append("kind", u.kind);
            fd.append("file", u.file, u.file.name);
            await api.post(`/shipments/${newId}/photos/upload`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
            ok += 1;
          } catch (err) {
            fail += 1;
          }
        }
        toast.dismiss("ship-photos");
      }

      let completedNow = true;
      try {
        const { data: ord } = await api.get(`/orders/${orderId}`);
        completedNow = ord?.stage === "tamamlandi";
      } catch { }
      if (fail) {
        toast.warning(`Sevkiyat kaydedildi. ${ok} fotoğraf yüklendi, ${fail} başarısız.`);
      } else if (completedNow) {
        toast.success(
          uploads.length
            ? `Sevkiyat tamamlandı — ${uploads.length} fotoğraf yüklendi ✔ Sipariş kapandı, muhasebe bilgilendirildi.`
            : "Sevkiyat tamamlandı ✔ Sipariş kapandı, muhasebe bilgilendirildi."
        );
      } else {
        toast.success("Kısmi sevkiyat kaydedildi ✔ Kalan kalemler için tekrar sevk oluşturabilirsiniz.");
      }
      onSaved?.();
      reset();
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const finishAndClose = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-50 flex items-center gap-2">
            <Truck className="w-5 h-5 text-blue-500" /> Yeni Sevkiyat Kaydı
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="mode-kargo" onClick={() => setMode("kargo")}
              className={`p-3 rounded-sm border text-sm font-semibold flex items-center justify-center gap-2 ${mode === "kargo" ? "border-blue-500 bg-blue-950/40 text-blue-300" : "border-slate-800 bg-slate-900 text-slate-400"}`}>
              <Truck className="w-4 h-4" /> Kargo
            </button>
            <button data-testid="mode-elden" onClick={() => setMode("elden")}
              className={`p-3 rounded-sm border text-sm font-semibold flex items-center justify-center gap-2 ${mode === "elden" ? "border-blue-500 bg-blue-950/40 text-blue-300" : "border-slate-800 bg-slate-900 text-slate-400"}`}>
              <UserCheck className="w-4 h-4" /> Elden Teslim
            </button>
          </div>

          <div>
            <Label className="text-xs uppercase text-slate-500 font-mono">Sipariş</Label>
            <Select value={orderId} onValueChange={setOrderId}>
              <SelectTrigger data-testid="ship-order-select" className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm mt-1">
                <SelectValue placeholder="Sipariş seçin" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800 max-h-72">
                {orders.map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.order_no} — {o.customer_name} · {o.quantity} adet</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <ShipmentLineChecklist
            orderId={orderId}
            onSummaryChange={handleSummaryChange}
          />

          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Teslim Alan / Gönderen" value={signerName} onChange={setSignerName} />
            <div>
              <Label className="text-xs uppercase text-slate-500 font-mono">Rol</Label>
              <Select value={signerRole} onValueChange={setSignerRole}>
                <SelectTrigger className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="musteri">Müşteri</SelectItem>
                  <SelectItem value="alici">Alıcı</SelectItem>
                  <SelectItem value="kurye">Kurye</SelectItem>
                  <SelectItem value="depo">Depo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border border-slate-800 rounded-sm p-3 bg-slate-900/40 grid grid-cols-2 md:grid-cols-3 gap-3">
            <FieldInput label="Ürün Özeti" value={productSummary} onChange={setProductSummary} />
            <FieldInput label="Renk" value={color} onChange={setColor} />
            <FieldInput label="Beden Özeti" value={sizeSummary} onChange={setSizeSummary} placeholder="S:20, M:40" />
            <FieldInput label="Paket Sayısı" type="number" value={packageCount} onChange={setPackageCount} />
            <FieldInput label="Koli Sayısı" type="number" value={boxCount} onChange={setBoxCount} />
            <FieldInput label="Hazırlayan" value={preparedBy} onChange={setPreparedBy} />
            <FieldInput label="İrsaliye No" value={waybillNo} onChange={setWaybillNo} />
            <FieldInput label="Fatura No" value={invoiceNo} onChange={setInvoiceNo} />
          </div>

          {mode === "kargo" ? (
            <div className="space-y-3 border border-slate-800 rounded-sm p-3 bg-slate-900/50">
              <div>
                <Label className="text-xs uppercase text-slate-500 font-mono">Kargo Firması</Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {COURIER_TEMPLATES.map((t) => (
                    <button key={t.name} data-testid={`courier-${t.name.replace(/\s+/g, "-")}`} onClick={() => applyCourierTemplate(t.name)}
                      className={`text-xs px-2.5 py-1 rounded-sm border ${courierName === t.name ? "border-blue-500 bg-blue-950/40 text-blue-300" : "border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-600"}`}>
                      {t.name}
                    </button>
                  ))}
                </div>
                <Input data-testid="ship-courier-name" value={courierName} onChange={(e) => setCourierName(e.target.value)}
                  className="bg-slate-950 border-slate-800 text-slate-100 rounded-sm mt-2" placeholder="Veya elle girin" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FieldInput label="Takip No" value={trackingNo} onChange={setTrackingNo} placeholder="123456789" />
                <FieldInput label="Takip Linki" value={trackingUrl} onChange={setTrackingUrl} placeholder="https://…" />
              </div>
            </div>
          ) : (
            <div className="space-y-3 border border-slate-800 rounded-sm p-3 bg-slate-900/50">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase text-slate-500 font-mono">Konum (GPS)</Label>
                <Button data-testid="ship-gps-btn" onClick={captureGps} type="button" size="sm" variant="outline"
                  className="border-blue-900 bg-blue-950/30 text-blue-300 hover:bg-blue-950/60 rounded-sm h-7 text-xs">
                  <MapPin className="w-3.5 h-3.5 mr-1" /> {gps ? "Yenile" : "Konumu Al"}
                </Button>
              </div>
              {gps && (
                <div className="text-xs text-slate-400 font-mono bg-slate-950 border border-slate-800 rounded-sm p-2">
                  {gps.lat.toFixed(6)}, {gps.lng.toFixed(6)} <span className="text-slate-500">(±{Math.round(gps.acc)}m)</span>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs uppercase text-slate-500 font-mono flex items-center gap-1.5">
                    <SignatureIcon className="w-3.5 h-3.5" /> İmza
                  </Label>
                  <button data-testid="ship-clear-sig" type="button" onClick={() => sigRef?.clear()} className="text-xs text-slate-500 hover:text-blue-400">Temizle</button>
                </div>
                <div className="bg-white rounded-sm border border-slate-700 overflow-hidden touch-none">
                  <SignatureCanvas ref={(r) => setSigRef(r)} penColor="#0f172a"
                    canvasProps={{ "data-testid": "ship-signature-canvas", className: "w-full h-40" }} />
                </div>
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase text-slate-500 font-mono">Notlar / Açıklama</Label>
            <Textarea data-testid="ship-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Sevkiyata dair açıklama, teslim koşulları vb."
              className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm mt-1 resize-none" />
          </div>

          <div className="space-y-4 border-t border-slate-800 pt-4">
            <div className="text-xs uppercase tracking-widest font-mono text-blue-300 border-b border-blue-900/40 pb-1.5">
              📎 Sevk Evrağı & Kargo Fotoğrafları
            </div>
            <BufferedPhotoPicker kind="document" files={docFiles} onChange={setDocFiles} />
            <BufferedPhotoPicker kind="cargo" files={cargoFiles} onChange={setCargoFiles} />
            <p className="text-[10px] text-slate-500 font-mono">
              Fotoğraflar sevkiyatı kaydettiğinizde otomatik olarak sıkıştırılıp yüklenir; ayrı bir işlem gerektirmez.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => finishAndClose()}
            className="border-slate-800 bg-slate-900 text-slate-300 hover:bg-slate-800 rounded-sm">
            İptal
          </Button>
          <Button data-testid="ship-save-btn" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 rounded-sm">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            {docFiles.length + cargoFiles.length > 0
              ? `Sevkiyatı Kaydet & ${docFiles.length + cargoFiles.length} Fotoğraf Yükle`
              : "Sevkiyatı Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// -------------------------------------------------------------------------
// Sevkiyat E-posta Bildirim Paneli — geçmiş + tekrar gönder
// -------------------------------------------------------------------------
function ShipmentEmailPanel({ shipmentId, deliveryMode }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/shipment-email/shipments/${shipmentId}/emails`);
      setItems(data?.items || []);
    } catch { }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [shipmentId]); // eslint-disable-line

  const resend = async (kind) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/shipment-email/shipments/${shipmentId}/resend?kind=${kind}`);
      toast.success(`Bildirim ${data?.to || ""} adresine tekrar gönderildi (${data?.attachments || 0} ek).`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2 border-t border-slate-800 pt-4" data-testid="shipment-email-panel">
      <div className="text-xs uppercase tracking-widest font-mono text-emerald-300 border-b border-emerald-900/40 pb-1.5 flex items-center justify-between">
        <span>✉ Müşteri Bildirim Geçmişi</span>
        <div className="flex items-center gap-2 normal-case tracking-normal">
          <button className="text-[10px] bg-emerald-950 text-emerald-300 border border-emerald-800 rounded-sm px-2 py-0.5 hover:bg-emerald-900"
            disabled={busy} onClick={() => resend("shipped")} data-testid="resend-shipped-btn">
            {deliveryMode === "kargo" ? "Kargo Maili Tekrar Gönder" : "Sevk Maili Tekrar Gönder"}
          </button>
          <button className="text-[10px] bg-emerald-950 text-emerald-300 border border-emerald-800 rounded-sm px-2 py-0.5 hover:bg-emerald-900"
            disabled={busy} onClick={() => resend("delivered")} data-testid="resend-delivered-btn">
            Teslim Maili Tekrar Gönder
          </button>
        </div>
      </div>
      {loading && <div className="text-slate-500 text-xs">Yükleniyor…</div>}
      {!loading && items.length === 0 && (
        <div className="text-slate-500 text-xs">Bu sevkiyat için henüz mail gönderilmedi.</div>
      )}
      {items.map((m) => (
        <div key={m.id} className={`border rounded-sm p-2 text-xs ${m.success ? "border-emerald-900 bg-emerald-950/20" : "border-rose-900 bg-rose-950/20"}`}>
          <div className="flex items-center gap-2">
            <span className={`px-1.5 rounded-sm uppercase text-[9px] ${m.success ? "bg-emerald-900 text-emerald-200" : "bg-rose-900 text-rose-200"}`}>
              {m.success ? "başarılı" : "hata"}
            </span>
            <span className="text-slate-400 font-mono">{new Date(m.sent_at).toLocaleString("tr-TR")}</span>
            <span className="uppercase text-[9px] text-slate-400">{m.kind}</span>
            <span className="text-slate-300 truncate flex-1">{m.subject}</span>
          </div>
          <div className="text-slate-500 mt-1 flex flex-wrap gap-x-4">
            <span>Alıcı: <b className="text-slate-300">{m.to || "—"}</b></span>
            <span>Gönderen: {m.sent_by_name}</span>
            {m.attachments?.length > 0 && <span>Ek: {m.attachments.length} dosya ({m.attachments.slice(0, 3).join(", ")}{m.attachments.length > 3 ? "…" : ""})</span>}
          </div>
          {m.error && <div className="text-rose-300 mt-1">Hata: {m.error}</div>}
        </div>
      ))}
    </div>
  );
}
